import { formatPrice } from '@zetter/shared';
import { LANDING_COUNT, TEMPLATE_TOTAL } from './catalog.js';
import type { PublicData } from './public-data.js';

/**
 * Частые вопросы: один источник на страницу и на разметку.
 *
 * `FAQPage` из schema.org — это обещание площадке, что ровно этот текст human
 * видит на странице. Разметка, разошедшаяся с видимым текстом, не просто
 * бесполезна: за неё снимают расширенные результаты целиком. Держать вопросы
 * в шаблоне, а их копию в генераторе разметки значит гарантировать расхождение
 * на первой же правке формулировки — поэтому текст живёт здесь, а лендинг и
 * `seo.ts` его читают.
 *
 * Заодно это отвечает на запросы, которые набирают словами: «как сделать
 * коммерческое предложение», «можно ли собрать лендинг», «в каких форматах
 * выгружается». Ответ должен быть самодостаточным — его цитируют вырванным из
 * страницы и поиском, и ассистентом.
 */
export interface FaqItem {
  q: string;
  a: string;
}

/**
 * Берёт не весь `PublicData`, а только то, чем пользуется: цены и реквизиты.
 * Требуй мы целиком — каждый новый раздел витрины ломал бы вызов у страниц,
 * которым он не нужен.
 */
export function faqItems(data: Pick<PublicData, 'pricing' | 'legal'>): FaqItem[] {
  const credit = data.pricing?.creditKopecks ?? null;

  /*
   * Цена называется, только если она известна. Сборка без доступа к API
   * оставляет ответ без числа, а не с прочерком: прочерк процитируют так же
   * охотно, как цифру.
   */
  const price = credit === null
    ? 'Считается в документах, а не в токенах: у документа одна цена, и это вся его цена.'
    : `${formatPrice(credit)} за документ, и это вся его цена.`;

  return [
    {
      q: 'Можно попробовать до регистрации?',
      a:
        'Задачу можно описать прямо на главной, не заводя аккаунт: напишите, что нужно, или ' +
        'возьмите готовую заготовку и поправьте под себя. Текст сохранится, и после ' +
        'регистрации он уже будет стоять в поле — набирать заново не придётся. Сам документ ' +
        'собирается после того, как аккаунт заведён: за сборку списывается документ с ' +
        'баланса, а он у аккаунта.',
    },
    {
      q: 'Как сделать коммерческое предложение в Keypi?',
      a:
        'Приложите материалы: бриф, прайс, запись созвона. Агент прочитает файлы, расшифрует ' +
        'аудио по говорящим, задаст недостающие вопросы и соберёт документ. Первая версия ' +
        'готова примерно через 15 минут, весь цикл с правками — около часа.',
    },
    {
      q: 'Можно ли собрать лендинг, а не только КП?',
      a:
        `Да. В каталоге ${LANDING_COUNT} шаблонов лендингов — продукт, SaaS-сервис, ` +
        'онлайн-школа, клиника, кофейня, спортзал, жилой комплекс, студия, ферма, — а кроме ' +
        'них презентации, кейсы, прайсы, меню, флаеры, афиши, отчёты и счета. Всего ' +
        `${TEMPLATE_TOTAL} готовых шаблонов, и можно начать с чистого листа.`,
    },
    {
      q: 'Сколько стоит один документ?',
      a:
        `${price} Списывается на первой сборке, правки к готовому документу бесплатны. ` +
        'Подписка включает пакет документов на месяц, перерасход докупается по той же цене.',
    },
    {
      q: 'В каких форматах выгружается документ?',
      a:
        'Публичная ссылка, PDF и PPTX — одной и той же кнопкой. Ссылка всегда показывает ' +
        'актуальную версию: поправили после отправки — клиент уже видит новое. Отзывается ' +
        'она в один клик.',
    },
    {
      q: 'Нужен ли дизайнер или верстальщик?',
      a:
        'Нет. Правки говорятся словами — в чате или голосом, без ТЗ. Текст и блоки можно ' +
        'поправить руками прямо в превью: это бесплатно и не ждёт агента. Каждая правка ' +
        'сохраняется отдельной версией, откат — в один клик.',
    },
    {
      q: 'Как сохранить фирменный стиль компании?',
      a:
        'Сохраните готовый проект шаблоном организации — дальше агент собирает по нему, и ' +
        'стиль не разъезжается от документа к документу. Свои шаблоны видны только вашей ' +
        'команде.',
    },
    {
      q: 'Есть ли интеграция с amoCRM?',
      a:
        'Да. Виджет ставится из амоМаркета, и документ собирается прямо в карточке сделки — ' +
        'не выходя из CRM. Kommo поддерживается так же.',
    },
    {
      q: 'Что происходит с данными клиентов?',
      a:
        'Проекты видны только вашей организации, доступ уволившегося сотрудника закрывается ' +
        'сразу, а публичная ссылка отзывается в один клик. Что именно обрабатывается и ' +
        'зачем — в политике обработки персональных данных.',
    },
  ];
}
